/**
 * @file OPM-CV 编辑器左上角搜索面板（对齐 OPM 编辑器功能）
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Input, Select, Typography } from 'antd';
import { ClearOutlined, SearchOutlined, UndoOutlined } from '@ant-design/icons';
import type { Core } from 'cytoscape';
import { cy as cyInstance } from './DiagramCanvas';
import { smartFitView } from '../controller/general';

const { Text } = Typography;
const { Option } = Select;

interface SearchResultItem {
  id: string;
  label: string;
  type?: string;
}

const SearchPanel: React.FC = () => {
  const [searchKeyword, setSearchKeyword] = useState('');
  const [jumpCount, setJumpCount] = useState(1);
  const [searchResults, setSearchResults] = useState<SearchResultItem[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [currentNodeId, setCurrentNodeId] = useState<string | null>(null);
  const [originalDisplay, setOriginalDisplay] = useState<
    Record<string, string>
  >({});
  const originalDisplayRef = useRef<Record<string, string>>({});
  const searchInputRef = useRef<any>(null);

  const getCy = (): Core | null => cyInstance || null;

  // 搜索定位：高亮目标节点（对齐左侧“本体组成元素列表”的交互）
  const highlightTargetNode = (cy: Core, nodeId: string) => {
    try {
      const node = cy.getElementById(nodeId);
      if (!node || node.empty()) return;
      cy.elements().unselect();
      node.select();
    } catch (error) {
      console.warn('高亮节点失败:', error);
    }
  };

  // 获取节点显示名称
  const getDisplayName = (node: any): string => {
    if (!node) return '未命名节点';
    const label =
      typeof node.data === 'function' ? node.data('label') : node.data?.label;
    return label || node.id || '未命名节点';
  };

  // 根据节点类型给出颜色
  const getNodeTypeColor = (type: string) => {
    const map: Record<string, string> = {
      object: '#70E483',
      process: '#3BC3FF',
      state: '#FAAD14',
    };
    return map[type] || '#999';
  };

  const getNodeTypeName = (type: string) => {
    const map: Record<string, string> = {
      object: '对象',
      process: '过程',
      state: '状态',
    };
    return map[type] || type || '节点';
  };

  // 生成邻接表（仅按边连通；父子关系不计跳数）
  const buildAdjacency = (cy: Core) => {
    const adjacency = new Map<string, Set<string>>();

    cy.edges().forEach(edge => {
      const source = edge.data('source');
      const target = edge.data('target');
      if (!adjacency.has(source)) adjacency.set(source, new Set());
      if (!adjacency.has(target)) adjacency.set(target, new Set());
      adjacency.get(source)!.add(target);
      adjacency.get(target)!.add(source);
    });

    return adjacency;
  };

  // BFS 计算指定跳数内的节点
  const getNodesWithinJumps = (
    cy: Core,
    startNodeId: string,
    maxJumps: number
  ): Set<string> => {
    const adjacency = buildAdjacency(cy);
    const visited = new Set<string>();
    const queue: Array<{ id: string; depth: number }> = [
      { id: startNodeId, depth: 0 },
    ];

    while (queue.length > 0) {
      const { id, depth } = queue.shift()!;
      if (visited.has(id) || depth > maxJumps) continue;
      visited.add(id);
      if (depth === maxJumps) continue;

      const neighbors = adjacency.get(id);
      neighbors?.forEach(nextId => {
        if (!visited.has(nextId)) {
          queue.push({ id: nextId, depth: depth + 1 });
        }
      });
    }

    // 保证状态节点的父对象也被显示，但不把父子关系算作跳数
    visited.forEach(nodeId => {
      const node = cy.getElementById(nodeId);
      const parentId = node.data('parent');
      if (parentId) {
        visited.add(parentId);
      }
    });

    return visited;
  };

  // 记录初始显示状态，便于清除搜索时还原
  const ensureDisplaySnapshot = (cy: Core) => {
    // 使用 ref 记录快照，避免 setState 异步导致“已过滤但无法还原”的偶发现象
    if (Object.keys(originalDisplayRef.current).length > 0) return;
    const snapshot: Record<string, string> = {};
    cy.elements().forEach(ele => {
      snapshot[ele.id()] = ele.data('display') || 'element';
    });
    originalDisplayRef.current = snapshot;
    setOriginalDisplay(snapshot);
  };

  // 应用跳数过滤并定位
  const applyJumpFilter = (nodeId: string, jumps: number) => {
    const cy = getCy();
    if (!cy) return;

    // 如果当前图层不存在该节点，避免把所有节点隐藏导致“空画布”（常见于图层切换/渲染中）
    const startNode = cy.getElementById(nodeId);
    if (startNode.empty()) {
      handleClearSearch();
      return;
    }

    // 先恢复所有隐藏元素，保证搜索时不漏节点/边
    cy.elements().forEach(ele => {
      ele.data({ display: 'element' });
    });
    ensureDisplaySnapshot(cy);
    const visibleNodeIds = getNodesWithinJumps(cy, nodeId, jumps);

    cy.nodes().forEach(node => {
      if (node.data('isMarkerLabel')) return;
      const shouldShow = visibleNodeIds.has(node.id());
      node.data({ display: shouldShow ? 'element' : 'none' });
    });

    cy.edges().forEach(edge => {
      const source = edge.data('source');
      const target = edge.data('target');
      const shouldShow =
        visibleNodeIds.has(source) && visibleNodeIds.has(target);
      edge.data({ display: shouldShow ? 'element' : 'none' });
    });

    const visibleElements = cy
      .elements()
      .filter(ele => ele.data('display') !== 'none');

    // 重置视图时以当前目标节点为中心，保证跳数切换后仍能看见
    const target = cy.getElementById(nodeId);
    if (target && !target.empty()) {
      cy.center(target);
    }
    smartFitView(cy, 80);
    // 高亮目标节点，方便观察从该节点出发的跳数范围
    highlightTargetNode(cy, nodeId);
  };

  // 搜索联想
  const handleSearch = () => {
    const cy = getCy();
    if (!cy) {
      setSearchResults([]);
      return;
    }

    const keyword = searchKeyword.trim().toLowerCase();
    if (!keyword) {
      setSearchResults([]);
      return;
    }

    const results = cy
      .nodes()
      .filter(node => {
        if (node.data('isMarkerLabel')) return false;
        const label = node.data('label') || '';
        return (
          String(label).toLowerCase().includes(keyword) ||
          node.id().toLowerCase().includes(keyword)
        );
      })
      .slice(0, 10)
      .map(node => ({
        id: node.id(),
        label: getDisplayName(node),
        type: node.data('type'),
      }));

    setSearchResults(results);
    setShowSearchResults(true);
  };

  const handleSearchBlur = () => {
    setTimeout(() => setShowSearchResults(false), 150);
  };

  // 选择搜索结果
  const selectSearchResult = (nodeId: string) => {
    const cy = getCy();
    if (!cy) return;

    const target = cy.getElementById(nodeId);
    if (target.empty()) return;

    setCurrentNodeId(nodeId);
    setSearchKeyword(getDisplayName(target));
    setShowSearchResults(false);

    applyJumpFilter(nodeId, jumpCount);
  };

  const handleJumpChange = (value: number) => {
    setJumpCount(value);
    if (currentNodeId) {
      applyJumpFilter(currentNodeId, value);
    }
  };

  // 清除搜索并还原显示状态
  const handleClearSearch = () => {
    const cy = getCy();
    const snapshot = originalDisplayRef.current;
    if (cy && Object.keys(snapshot).length > 0) {
      cy.elements().forEach(ele => {
        const fallback = snapshot[ele.id()] || 'element';
        ele.data({ display: fallback });
      });
      // 清除搜索高亮（避免清除后仍残留选中样式）
      cy.elements().unselect();
    }

    originalDisplayRef.current = {};
    setOriginalDisplay({});
    setCurrentNodeId(null);
    setSearchKeyword('');
    setSearchResults([]);
    setShowSearchResults(false);
  };

  // 暴露清除搜索函数供其他组件调用（如切换图表时）
  useEffect(() => {
    (window as any).__clearSearchAndRestore = handleClearSearch;
    return () => {
      delete (window as any).__clearSearchAndRestore;
    };
  }, [originalDisplay]);

  // 显示所有节点与边，同时恢复隐藏的父子关系
  const handleShowAll = () => {
    const cy = getCy();
    if (!cy) return;

    cy.elements().forEach(ele => {
      if (ele.isNode() && ele.data('lastParent')) {
        ele.move({ parent: ele.data('lastParent') });
      }
      ele.data({ display: 'element' });
    });

    originalDisplayRef.current = {};
    setOriginalDisplay({});
    setCurrentNodeId(null);
    setSearchKeyword('');
    setSearchResults([]);
    setShowSearchResults(false);

    smartFitView(cy, 80);
  };

  // 复位视图到当前可见元素
  const handleResetView = () => {
    const cy = getCy();
    if (!cy) return;
    smartFitView(cy, 80);
  };

  useEffect(() => {
    handleSearch();
  }, [searchKeyword]);

  const currentNodeLabel = useMemo(() => {
    const cy = getCy();
    if (!cy || !currentNodeId) return '';
    const node = cy.getElementById(currentNodeId);
    return getDisplayName(node);
  }, [currentNodeId]);

  return (
    <Card size="small">
      <div className="search-panel-body">
        {/* 搜索输入 */}
        <div className="search-container" style={{ position: 'relative' }}>
          <Input
            ref={searchInputRef}
            placeholder="输入节点名称搜索..."
            value={searchKeyword}
            onChange={e => setSearchKeyword(e.target.value)}
            onFocus={() => {
              handleSearch();
              setShowSearchResults(true);
            }}
            onClick={() => {
              // 重新触发一次搜索，保证节点重命名后下拉结果最新
              handleSearch();
              setShowSearchResults(true);
            }}
            onBlur={handleSearchBlur}
            onPressEnter={() => {
              if (searchResults.length > 0) {
                selectSearchResult(searchResults[0].id);
              }
            }}
            suffix={
              <Button type="text" icon={<SearchOutlined />} size="small" />
            }
          />

          {/* 搜索结果下拉列表 */}
          {showSearchResults && searchResults.length > 0 && (
            <div className="search-results-dropdown">
              {searchResults.map(item => (
                <div
                  key={item.id}
                  className="search-result-item"
                  onClick={() => selectSearchResult(item.id)}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <div
                    className="search-result-type-indicator"
                    style={{
                      backgroundColor: getNodeTypeColor(item.type || ''),
                    }}
                  />
                  <span className="search-result-name">{item.label}</span>
                  <span className="search-result-type">
                    {getNodeTypeName(item.type || '')}
                  </span>
                </div>
              ))}
            </div>
          )}

          {showSearchResults && searchKeyword && searchResults.length === 0 && (
            <div className="search-results-dropdown search-no-results">
              未找到匹配的节点
            </div>
          )}
        </div>

        {/* 跳数控制 */}
        <div className="jump-steps-container">
          <Text className="jump-steps-label">跳数:</Text>
          <Select
            size="small"
            value={jumpCount}
            onChange={handleJumpChange}
            style={{ width: 80 }}
          >
            <Option value={1}>1</Option>
            <Option value={2}>2</Option>
            <Option value={3}>3</Option>
          </Select>
        </div>

        {/* 操作按钮 */}
        {(searchKeyword || currentNodeId) && (
          <div className="search-actions">
            <Button
              size="small"
              onClick={handleClearSearch}
              icon={<ClearOutlined />}
            >
              清除搜索
            </Button>
            <Button size="small" type="primary" onClick={handleShowAll}>
              显示所有
            </Button>
            <Button
              size="small"
              icon={<UndoOutlined />}
              onClick={handleResetView}
            >
              重置视图
            </Button>
          </div>
        )}

        {/* 当前搜索结果提示 */}
        {currentNodeId && currentNodeLabel && (
          <div className="search-current-result">
            当前搜索: {currentNodeLabel}（{jumpCount} 跳范围）
          </div>
        )}
      </div>
    </Card>
  );
};

export default SearchPanel;
